import { Banner } from '@postscript/components';
import { AnnouncementType } from 'components/admin/Announcements/utils';
import {
  GlobalBanner,
  useBanners,
} from 'components/GlobalBanners/globalBanners';
import { AnnouncementPayload } from 'controllers/contexts/announcements';
import { useUser } from 'controllers/contexts/user';
import { useEffect } from 'react';
import styled from 'styled-components';
import { logButtonClickEvent, logEvent, PRODUCT_AREAS } from 'utils/events';

// Maximum number of emergency banners to display
const MAX_EMERGENCY_BANNERS = 1;

const StyledBannerContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: var(--spacing-3);
`;

/**
 * Custom hook to track when an emergency banner is displayed
 */
const useTrackEmergencyBannerDisplay = (
  id: string | number,
  text: string | React.ReactNode,
  hasCta: boolean,
) => {
  useEffect(() => {
    const announcementText =
      typeof text === 'string' ? text : 'Has custom content';

    logEvent('emergency banner displayed', {
      announcement_id: id,
      announcement_text: announcementText,
      has_cta: hasCta,
      location: 'app top',
      product_area: PRODUCT_AREAS.GLOBAL,
    });
  }, [id]);
};

/**
 * Helper to log emergency banner button clicks with standardized tracking
 */
const logEmergencyBannerClick = (
  event: React.MouseEvent<HTMLButtonElement>,
  announcementId: string | number,
  buttonType: 'primary' | 'secondary',
) => {
  logButtonClickEvent(event, {
    announcement_id: announcementId,
    location: 'emergency banner',
    product_area: PRODUCT_AREAS.GLOBAL,
    button_type: buttonType,
  });
};

/**
 * Renders a legacy ps_announcement emergency banner
 * These are admin-controlled, service-wide emergencies (PRIORITY 1)
 */
const PsAnnouncementBanner = ({
  announcement,
}: {
  announcement: AnnouncementPayload;
}) => {
  useTrackEmergencyBannerDisplay(
    announcement.id,
    announcement.text,
    Boolean(announcement.cta_url),
  );

  const handleCtaClick = announcement.cta_url
    ? (event: React.MouseEvent<HTMLButtonElement>) => {
        logEmergencyBannerClick(event, announcement.id, 'primary');

        const newWindow = window.open(
          announcement.cta_url,
          '_blank',
          'noopener,noreferrer',
        );
        if (newWindow) newWindow.opener = null;
      }
    : undefined;

  return (
    <Banner
      variant="critical"
      bodyText={announcement.text || ''}
      primaryAction={
        announcement.cta_url && announcement.cta_url_text
          ? {
              text: announcement.cta_url_text,
              onClick: handleCtaClick,
            }
          : undefined
      }
    />
  );
};

/**
 * Renders a GlobalBanner emergency banner
 * These are app-generated based on individual user state (PRIORITY 2)
 */
const GlobalEmergencyBanner = ({ banner }: { banner: GlobalBanner }) => {
  useTrackEmergencyBannerDisplay(
    banner.id,
    banner.data.bodyText,
    Boolean(banner.data.primaryAction),
  );

  // Helper to wrap actions with tracking
  const wrapActionWithTracking = (
    action:
      | typeof banner.data.primaryAction
      | typeof banner.data.secondaryAction,
    buttonType: 'primary' | 'secondary',
  ) => {
    if (!action) return undefined;

    return {
      ...action,
      onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
        logEmergencyBannerClick(event, banner.id, buttonType);
        action.onClick?.(event);
      },
    };
  };

  return (
    <Banner
      {...banner.data}
      primaryAction={wrapActionWithTracking(
        banner.data.primaryAction,
        'primary',
      )}
      secondaryAction={wrapActionWithTracking(
        banner.data.secondaryAction,
        'secondary',
      )}
    />
  );
};

/**
 * Primary Emergency Banner component
 * Displays emergency banners at the top of the app (up to MAX_EMERGENCY_BANNERS)
 * Priority: ps_announcements (admin-controlled) > GlobalBanners (app-generated)
 */
const EmergencyBanner = () => {
  const { user } = useUser();
  const { banners } = useBanners();

  // PRIORITY 1: Get emergency ps_announcements (up to MAX_EMERGENCY_BANNERS)
  const emergencyPsAnnouncements = (user.ps_announcements || [])
    .filter(
      (announcement) =>
        announcement.announcement_type === AnnouncementType.EMERGENCY,
    )
    .slice(0, MAX_EMERGENCY_BANNERS);

  // PRIORITY 2: If we haven't reached the max, get emergency globalBanners
  const remainingSlots =
    MAX_EMERGENCY_BANNERS - emergencyPsAnnouncements.length;
  const emergencyGlobalBanners =
    remainingSlots > 0
      ? banners
          .filter(
            (banner) => banner?.announcementType === AnnouncementType.EMERGENCY,
          )
          .slice(0, remainingSlots)
      : [];

  // Combine banners in priority order
  const allEmergencyBanners = [
    ...emergencyPsAnnouncements,
    ...emergencyGlobalBanners,
  ];

  // No emergency banners to show
  if (allEmergencyBanners.length === 0) {
    return null;
  }

  // Render all emergency banners in a container
  return (
    <StyledBannerContainer>
      {emergencyPsAnnouncements.map((announcement) => (
        <PsAnnouncementBanner
          key={`ps-${announcement.id}`}
          announcement={announcement}
        />
      ))}
      {emergencyGlobalBanners.map((banner) => (
        <GlobalEmergencyBanner key={`global-${banner.id}`} banner={banner} />
      ))}
    </StyledBannerContainer>
  );
};

export default EmergencyBanner;
